'use client'

import { toast } from 'sonner'

import { NotificationToastCard } from '@/components/ui/notification-toast'
import { formatOrderQuantity } from '@/lib/order-denomination'
import { formatSymbol } from '@/lib/utils/format-util'
import { humanizeReason } from '@/lib/utils/humanize-reason'
import type {
  ClassifiedNotification,
  NotificationSeverity,
} from '@/service/ws/classify-notification'
import {
  executionErrorToastId,
  markRejectionSurfaced,
  wasRejectionSurfaced,
} from '@/service/ws/reject-dedupe'
import type { ResearchNotificationEntry } from '@/state/research'
import type { Order } from '@/types/generated/common/trading'
import type {
  OrderExecutionError,
  OrderResponse,
  OrderValidationError,
  ServiceNotification,
} from '@/types/generated/notifications'

export function OrderResponseDetail({
  response,
  isError,
}: {
  response: OrderResponse
  isError: boolean
}) {
  const rows: Array<[string, string]> = []
  if (response.quantity) rows.push(['Qty', formatOrderQuantity(response, response.quantity)])
  // Rejections carry no fill data worth showing — filledQuantity/averagePrice often
  // come through as the venue's "0" default rather than being unset, which would
  // otherwise render a misleading "Filled: 0 / Avg Price: 0" grid on an error toast.
  if (!isError) {
    if (response.filledQuantity)
      rows.push(['Filled', formatOrderQuantity(response, response.filledQuantity)])
    if (response.averagePrice) rows.push(['Avg Price', response.averagePrice])
    if (response.price) rows.push(['Price', response.price])
  }
  if (rows.length === 0) return null

  return (
    <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-baseline gap-1 text-xs">
          <span className="text-foreground/40">{label}:</span>
          <span className="text-foreground font-medium">{value}</span>
        </div>
      ))}
    </div>
  )
}

export function showOrderResponseToast(
  response: OrderResponse,
  classified: ClassifiedNotification,
  timestamp: string,
  accountId: string,
  accountName?: string,
  exchange?: string
) {
  // Only surface `message` (the rejection reason) for errors — for success/placed/
  // cancelled the detail grid + symbol badge already say everything worth saying, and
  // classified.message's composed summary would just repeat it.
  const isError = classified.category === 'order_error'

  toast.custom(
    () => (
      <NotificationToastCard
        severity={classified.severity}
        title={classified.title}
        message={isError ? classified.message : undefined}
        symbol={response.symbol}
        accountName={accountName}
        exchange={exchange}
        detail={<OrderResponseDetail response={response} isError={isError} />}
        timestamp={Number(timestamp)}
        size="detailed"
      />
    ),
    {
      id: orderLifecycleToastId(accountId, response.orderId),
      duration: 5000,
      position: 'bottom-left',
    }
  )
}

function orderLifecycleToastId(accountId: string, orderId: string) {
  return `order-${accountId}-${orderId}`
}

function showOrderPlacedToast(
  response: OrderResponse,
  classified: ClassifiedNotification,
  accountId: string,
  accountName?: string,
  exchange?: string
) {
  toast.custom(
    () => (
      <NotificationToastCard
        severity={classified.severity}
        title={classified.title}
        symbol={response.symbol}
        accountName={accountName}
        exchange={exchange}
        size="compact"
      />
    ),
    {
      // Every lifecycle update for one account/order pair shares an ID, so a
      // fill/cancel/reject replaces its earlier placement acknowledgement.
      id: orderLifecycleToastId(accountId, response.orderId),
      duration: 3000,
      position: 'bottom-left',
    }
  )
}

/**
 * Rejection surfaced from the account ORDERS stream rather than a
 * `ServiceNotification`. Venues differ in what they emit on a refusal: some
 * publish an `OrderResponse` notification, but several (Toss among them) only
 * publish the unified Order carrying `ORDER_STATUS_REJECTED` + `rejectReason`,
 * so the reason reached the orders table and never the user.
 *
 * The sonner `id` deliberately matches the ServiceNotification order lifecycle
 * toast - a venue that emits BOTH paths collapses to a single toast in place
 * instead of double-reporting one refusal.
 */
export function showOrderRejectedToast(
  order: Order,
  timestamp: number,
  accountName?: string,
  exchange?: string
) {
  toast.custom(
    () => (
      <NotificationToastCard
        severity="error"
        title="Order Rejected"
        // `rejectReason` is the venue's own words ("relative limit price is not
        // supported on Toss yet"). Without it a rejection is indistinguishable
        // from a dead button, which is the whole bug - so fall back to a
        // generic line only when the venue sent no reason at all.
        message={
          humanizeReason(order.rejectReason) || `${formatSymbol(order.symbol)} order rejected`
        }
        symbol={order.symbol}
        accountName={accountName}
        exchange={exchange}
        detail={
          order.quantity ? (
            <div className="mt-1.5 flex items-baseline gap-1 text-xs">
              <span className="text-foreground/40">Qty:</span>
              <span className="text-foreground font-medium">
                {formatOrderQuantity(order, order.quantity)}
              </span>
            </div>
          ) : undefined
        }
        timestamp={timestamp}
        size="detailed"
      />
    ),
    {
      id: orderLifecycleToastId(order.accountId, order.orderId),
      // Longer than the 5s used elsewhere: reject reasons are full sentences and
      // the user has to actually read this one to know what to change.
      duration: 8000,
      position: 'bottom-left',
    }
  )

  // Same refusal, other channel. This card is the richer of the two, so claim
  // the message (suppressing a notification still in flight) and clear one that
  // already landed. See `reject-dedupe` for why the text is the only shared key.
  // Guarded on there being a reason at all: with none there is nothing to match,
  // and `order-execution-` would dismiss an unrelated reasonless notification.
  if (order.rejectReason) {
    toast.dismiss(executionErrorToastId(order.rejectReason))
    markRejectionSurfaced(order.rejectReason)
  }
}

export function showOrderValidationErrorToast(
  error: OrderValidationError,
  classified: ClassifiedNotification,
  timestamp: string,
  accountName?: string,
  exchange?: string
) {
  toast.custom(
    () => (
      <NotificationToastCard
        severity={classified.severity}
        title={classified.title}
        message={classified.message}
        symbol={error.symbol}
        accountName={accountName}
        exchange={exchange}
        timestamp={Number(timestamp)}
        size="compact"
      />
    ),
    {
      id: `order-validation-${error.symbol}-${timestamp}`,
      duration: 5000,
      position: 'bottom-left',
    }
  )
}

export function showOrderExecutionErrorToast(
  error: OrderExecutionError,
  classified: ClassifiedNotification,
  timestamp: string,
  accountName?: string,
  exchange?: string
) {
  // The rejected-order frame already reported this one, with the symbol and
  // quantity this compact card lacks.
  if (wasRejectionSurfaced(error.errorMessage)) return

  toast.custom(
    () => (
      <NotificationToastCard
        severity={classified.severity}
        title={classified.title}
        message={classified.message}
        accountName={accountName}
        exchange={exchange}
        timestamp={Number(timestamp)}
        size="compact"
      />
    ),
    {
      // Keyed on the message, not the publish timestamp: it lets a
      // rejected-order frame dismiss this exact toast, and it collapses a venue
      // re-reporting one terminal condition (no margin, retried every 250ms by
      // an algo) into a single toast instead of twenty.
      id: executionErrorToastId(error.errorMessage),
      duration: 5000,
      position: 'bottom-left',
    }
  )
}

export function showOrderNotificationToast(
  notification: ServiceNotification,
  classified: ClassifiedNotification,
  accountName?: string,
  exchange?: string
) {
  const { orderResponse, orderValidationError, orderExecutionError, timestamp } = notification

  if (orderResponse) {
    if (classified.category === 'order_placed') {
      showOrderPlacedToast(orderResponse, classified, notification.accountId, accountName, exchange)
    } else {
      showOrderResponseToast(
        orderResponse,
        classified,
        timestamp,
        notification.accountId,
        accountName,
        exchange
      )
    }
  } else if (orderValidationError) {
    showOrderValidationErrorToast(
      orderValidationError,
      classified,
      timestamp,
      accountName,
      exchange
    )
  } else if (orderExecutionError) {
    showOrderExecutionErrorToast(orderExecutionError, classified, timestamp, accountName, exchange)
  }
}

export function showSimpleNotificationToast(
  severity: NotificationSeverity,
  title: string,
  symbol?: string,
  accountName?: string,
  exchange?: string
) {
  toast.custom(
    () => (
      <NotificationToastCard
        severity={severity}
        title={title}
        symbol={symbol}
        accountName={accountName}
        exchange={exchange}
        size="compact"
      />
    ),
    {
      id: `simple-${severity}-${title}-${symbol || ''}`,
      duration: 3000,
      position: 'bottom-left',
    }
  )
}

export function showAlgoLifecycleToast(
  classified: ClassifiedNotification,
  accountName?: string,
  exchange?: string
) {
  // No symbol badge: classified.title (algo.message, e.g. "LIMIT_CHASE BTC-PERPETUAL buy
  // started") already embeds the symbol. Passing it again steals width from the compact
  // card and truncates exactly the word that distinguishes "started" from "completed".
  showSimpleNotificationToast(
    classified.severity,
    classified.title,
    undefined,
    accountName,
    exchange
  )
}

export function showSecurityAlertToast(
  classified: ClassifiedNotification,
  timestamp: string,
  accountName?: string,
  exchange?: string
) {
  const id = `security-${timestamp}`
  toast.custom(
    () => (
      <NotificationToastCard
        severity={classified.severity}
        title={classified.title}
        message={classified.message}
        accountName={accountName}
        exchange={exchange}
        size="compact"
        onClose={() => toast.dismiss(id)}
      />
    ),
    { id, duration: Infinity, position: 'bottom-left' }
  )
}

export function showErrorToast(
  message: string,
  symbol?: string,
  accountName?: string,
  exchange?: string
) {
  showSimpleNotificationToast('error', message, symbol, accountName, exchange)
}

export function showResearchNotificationToast(
  entry: ResearchNotificationEntry,
  onOpen: () => void
) {
  toast.custom(
    () => (
      <button type="button" onClick={onOpen} className="text-left">
        <NotificationToastCard
          severity={entry.severity}
          title={entry.title}
          message={entry.message}
          timestamp={entry.timestamp}
          size="compact"
        />
      </button>
    ),
    {
      id: entry.id,
      duration: 8000,
      position: 'bottom-left',
    }
  )
}
